import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { useFees } from '../context/FeeContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COLORS = ['#2563eb', '#dc2626', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899'];

const AdminDashboardFull = ({ user, onLogout }) => {
  const { feeRates } = useFees();
  const [activeTab, setActiveTab] = useState('overview');
  const [platformStats, setPlatformStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [transactionReport, setTransactionReport] = useState(null);
  const [revenueReport, setRevenueReport] = useState(null);
  const [userGrowthReport, setUserGrowthReport] = useState(null);
  const [pendingBankTransfers, setPendingBankTransfers] = useState([]);
  const [pendingEscrowReleases, setPendingEscrowReleases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddAdminModal, setShowAddAdminModal] = useState(false);
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [showEditUserModal, setShowEditUserModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [newAdmin, setNewAdmin] = useState({ email: '', password: '', name: '' });
  const [newUser, setNewUser] = useState({ email: '', password: '', name: '', role: 'client', phone: '' });
  const [userFilter, setUserFilter] = useState('all');
  const [dateRange, setDateRange] = useState({ start: '', end: '', preset: 'all' });

  const getDatePreset = (preset) => {
    const today = new Date();
    const fmt = (d) => d.toISOString().split('T')[0];
    switch (preset) {
      case 'today':
        return { start: fmt(today), end: fmt(today), preset: 'today' };
      case 'week': {
        const start = new Date(today);
        start.setDate(today.getDate() - today.getDay());
        return { start: fmt(start), end: fmt(today), preset: 'week' };
      }
      case 'month': {
        const start = new Date(today.getFullYear(), today.getMonth(), 1);
        return { start: fmt(start), end: fmt(today), preset: 'month' };
      }
      case 'quarter': {
        const qStart = new Date(today.getFullYear(), Math.floor(today.getMonth() / 3) * 3, 1);
        return { start: fmt(qStart), end: fmt(today), preset: 'quarter' };
      }
      case 'year': {
        const start = new Date(today.getFullYear(), 0, 1);
        return { start: fmt(start), end: fmt(today), preset: 'year' };
      }
      default:
        return { start: '', end: '', preset: 'all' };
    }
  };

  useEffect(() => {
    fetchAllData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange.start, dateRange.end]);

  const fetchAllData = async () => {
    try {
      const dateParams = dateRange.start && dateRange.end 
        ? `?start_date=${dateRange.start}&end_date=${dateRange.end}` 
        : '';
      const [
        statsRes,
        usersRes,
        withdrawalsRes,
        transactionsRes,
        revenueRes,
        userGrowthRes,
        pendingTransfersRes,
        pendingEscrowRes
      ] = await Promise.all([
        axios.get(`${API}/admin/stats${dateParams}`),
        axios.get(`${API}/admin/users`),
        axios.get(`${API}/withdrawals/all`),
        axios.get(`${API}/admin/reports/transactions${dateParams}`),
        axios.get(`${API}/admin/reports/revenue${dateParams}`),
        axios.get(`${API}/admin/reports/user-growth${dateParams}`),
        axios.get(`${API}/admin/payments/pending-verification`).catch(() => ({ data: [] })),
        axios.get(`${API}/escrow/pending-release`).catch(() => ({ data: [] }))
      ]);

      setPlatformStats(statsRes.data);
      setUsers(usersRes.data);
      setWithdrawals(withdrawalsRes.data);
      setTransactionReport(transactionsRes.data);
      setRevenueReport(revenueRes.data);
      setUserGrowthReport(userGrowthRes.data);
      setPendingBankTransfers(pendingTransfersRes.data);
      setPendingEscrowReleases(pendingEscrowRes.data);
    } catch (error) {
      console.error('Error fetching admin data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleWithdrawalAction = async (withdrawalId, status) => {
    try {
      await axios.put(`${API}/withdrawals/${withdrawalId}`, {
        status: status,
        notes: status === 'approved' ? 'Approved by admin' : 'Rejected by admin'
      });
      fetchAllData();
    } catch (error) {
      alert('Failed to update withdrawal');
    }
  };

  const handleUserStatusUpdate = async (userId, status) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/status?status=${status}`);
      fetchAllData();
    } catch (error) {
      alert('Failed to update user status');
    }
  };

  const handleDeleteUser = async (userId) => {
    const userToDelete = users.find(u => u.id === userId);
    const confirmMsg = `⚠️ WARNING: This will permanently delete user "${userToDelete?.name}" and ALL their related data including:\n\n• Services posted\n• Jobs posted\n• Bookings (as client or provider)\n• Bids submitted\n• Notifications\n• Reviews\n• Messages\n• Transactions\n• Withdrawals\n• Payments\n\nThis action CANNOT be undone. Continue?`;
    
    if (!window.confirm(confirmMsg)) {
      return;
    }
    try {
      const response = await axios.delete(`${API}/admin/users/${userId}`);
      fetchAllData();
      alert(`✅ ${response.data.message}\n\nDeleted counts:\n${Object.entries(response.data.deleted_counts || {}).map(([k,v]) => `• ${k}: ${v}`).join('\n')}`);
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handleAddAdmin = async () => {
    try {
      await axios.post(`${API}/admin/create-admin`, newAdmin);
      setShowAddAdminModal(false);
      setNewAdmin({ email: '', password: '', name: '' });
      fetchAllData();
      alert('Admin created successfully!');
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to create admin');
    }
  };

  const handleAddUser = async () => {
    if (!newUser.email || !newUser.password || !newUser.name) {
      alert('Please fill in all required fields');
      return;
    }
    try {
      await axios.post(`${API}/admin/users`, newUser);
      setShowAddUserModal(false);
      setNewUser({ email: '', password: '', name: '', role: 'client', phone: '' });
      fetchAllData();
      alert('User created successfully!');
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to create user');
    }
  };

  const handleEditUser = async () => {
    if (!editingUser) return;
    try {
      const params = new URLSearchParams();
      if (editingUser.name) params.append('name', editingUser.name);
      if (editingUser.email) params.append('email', editingUser.email);
      if (editingUser.role) params.append('role', editingUser.role);
      if (editingUser.status) params.append('status', editingUser.status);
      if (editingUser.phone) params.append('phone', editingUser.phone);
      if (editingUser.bio) params.append('bio', editingUser.bio);
      
      await axios.put(`${API}/admin/users/${editingUser.id}?${params.toString()}`);
      setShowEditUserModal(false);
      setEditingUser(null);
      fetchAllData();
      alert('User updated successfully!');
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to update user');
    }
  };

  const openEditModal = (userToEdit) => {
    setEditingUser({ ...userToEdit });
    setShowEditUserModal(true);
  };

  const filteredUsers = users.filter(u => 
    userFilter === 'all' || u.role === userFilter
  );

  // Prepare chart data
  const chartData = transactionReport?.chart_data ? 
    Object.entries(transactionReport.chart_data).map(([date, data]) => ({
      date,
      amount: data.amount,
      commission: data.commission,
      count: data.count
    })) : [];

  const categoryData = revenueReport?.revenue_by_category ?
    Object.entries(revenueReport.revenue_by_category).map(([category, revenue]) => ({
      name: category,
      value: revenue
    })) : [];

  // User growth chart data
  const userGrowthData = userGrowthReport?.growth_by_date || [];

  // Role distribution data for pie chart
  const roleDistributionData = userGrowthReport?.role_distribution ?
    Object.entries(userGrowthReport.role_distribution)
      .filter(([_, count]) => count > 0)
      .map(([role, count]) => ({
        name: role.charAt(0).toUpperCase() + role.slice(1),
        value: count
      })) : [];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar user={user} onLogout={onLogout} />
        <div className="flex items-center justify-center h-96">
          <div className="text-xl font-semibold text-gray-600">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-dashboard-full">
      <Navbar user={user} onLogout={onLogout} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold bg-gradient-to-r from-orange-500 to-orange-600 bg-clip-text text-transparent">
            Platform Administration
          </h1>
          <button
            onClick={() => setShowAddAdminModal(true)}
            className="bg-gradient-to-r from-orange-500 to-orange-600 text-white px-6 py-2 rounded-lg hover:shadow-lg font-semibold"
          >
            + Add Administrator
          </button>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow-md mb-6">
          <div className="border-b border-gray-200">
            <nav className="flex -mb-px">
              {['overview', 'users', 'withdrawals', 'payments', 'reports'].map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`py-4 px-6 font-semibold border-b-2 transition ${
                    activeTab === tab
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                  data-testid={`admin-tab-${tab}`}
                >
                  {tab.charAt(0).toUpperCase() + tab.slice(1)}
                  {tab === 'payments' && pendingBankTransfers.length > 0 && (
                    <span className="ml-2 bg-red-500 text-white text-xs px-2 py-1 rounded-full">
                      {pendingBankTransfers.length}
                    </span>
                  )}
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && platformStats && (
          <div>
            {/* Date Range Filter */}
            <div className="bg-white p-4 rounded-lg shadow-md mb-6" data-testid="date-filter">
              <div className="flex flex-wrap items-center gap-3">
                <span className="text-sm font-semibold text-gray-700">Filter by:</span>
                <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
                  {[
                    { key: 'all', label: 'All Time' },
                    { key: 'today', label: 'Today' },
                    { key: 'week', label: 'This Week' },
                    { key: 'month', label: 'This Month' },
                    { key: 'quarter', label: 'Quarter' },
                    { key: 'year', label: 'This Year' },
                  ].map(p => (
                    <button
                      key={p.key}
                      onClick={() => setDateRange(getDatePreset(p.key))}
                      className={`px-3 py-1.5 rounded-md text-xs font-medium transition ${
                        dateRange.preset === p.key
                          ? 'bg-blue-600 text-white shadow'
                          : 'text-gray-600 hover:bg-gray-200'
                      }`}
                      data-testid={`filter-${p.key}`}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
                <div className="flex items-center gap-2 ml-auto">
                  <input
                    type="date"
                    value={dateRange.start}
                    onChange={(e) => setDateRange({ start: e.target.value, end: dateRange.end || e.target.value, preset: 'custom' })}
                    className="border rounded-md px-2 py-1.5 text-xs"
                    data-testid="filter-start-date"
                  />
                  <span className="text-gray-400 text-xs">to</span>
                  <input
                    type="date"
                    value={dateRange.end}
                    onChange={(e) => setDateRange({ start: dateRange.start || e.target.value, end: e.target.value, preset: 'custom' })}
                    className="border rounded-md px-2 py-1.5 text-xs"
                    data-testid="filter-end-date"
                  />
                </div>
                {dateRange.preset !== 'all' && (
                  <span className="text-xs text-blue-600 font-medium bg-blue-50 px-2 py-1 rounded">
                    {dateRange.start} - {dateRange.end}
                  </span>
                )}
              </div>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-blue-500">
                <h3 className="text-gray-500 text-sm font-medium">Total Users</h3>
                <p className="text-3xl font-bold text-blue-600 mt-2">{platformStats.total_users}</p>
                <p className="text-xs text-gray-500 mt-1">
                  {platformStats.total_providers} Providers • {platformStats.total_clients} Clients
                </p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-green-500">
                <h3 className="text-gray-500 text-sm font-medium">Platform Revenue ({feeRates.total_platform_rate}%)</h3>
                <p className="text-3xl font-bold text-green-600 mt-2">
                  {platformStats.currency} {platformStats.total_revenue.toFixed(2)}
                </p>
                <p className="text-xs text-gray-500 mt-1">Commission earned</p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-purple-500">
                <h3 className="text-gray-500 text-sm font-medium">Transaction Volume</h3>
                <p className="text-3xl font-bold text-purple-600 mt-2">
                  {platformStats.currency} {platformStats.total_transactions.toFixed(2)}
                </p>
                <p className="text-xs text-gray-500 mt-1">Total processed</p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-yellow-500">
                <h3 className="text-gray-500 text-sm font-medium">Pending Withdrawals</h3>
                <p className="text-3xl font-bold text-yellow-600 mt-2">{platformStats.pending_withdrawals}</p>
                <p className="text-xs text-gray-500 mt-1">Awaiting approval</p>
              </div>
            </div>

            {/* Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
              {/* Transaction Trend */}
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h2 className="text-xl font-bold mb-4">Transaction Trends</h2>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="amount" stroke="#2563eb" name="Total Amount" />
                    <Line type="monotone" dataKey="commission" stroke="#dc2626" name="Commission" />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              {/* User Role Distribution */}
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h2 className="text-xl font-bold mb-4">User Role Distribution</h2>
                {roleDistributionData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={roleDistributionData}
                        cx="50%"
                        cy="50%"
                        labelLine={true}
                        label={({name, value, percent}) => `${name}: ${value} (${(percent * 100).toFixed(0)}%)`}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {roleDistributionData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex items-center justify-center h-64 text-gray-500">
                    No user data available
                  </div>
                )}
              </div>
            </div>

            {/* User Growth Chart */}
            <div className="bg-white p-6 rounded-lg shadow-md mb-8 mt-6">
              <h2 className="text-xl font-bold mb-4">User Growth Over Time</h2>
              {userGrowthData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={userGrowthData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="cumulative" stroke="#10b981" name="Total Users" strokeWidth={2} />
                    <Line type="monotone" dataKey="new_users" stroke="#8b5cf6" name="New Signups" />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-64 text-gray-500">
                  No growth data available
                </div>
              )}
            </div>

            {/* Additional Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-gray-500 text-sm font-medium">Total Services</h3>
                <p className="text-2xl font-bold text-gray-800 mt-2">{platformStats.total_services}</p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-gray-500 text-sm font-medium">Total Bookings</h3>
                <p className="text-2xl font-bold text-gray-800 mt-2">{platformStats.total_bookings}</p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-gray-500 text-sm font-medium">Avg Commission/Transaction</h3>
                <p className="text-2xl font-bold text-gray-800 mt-2">
                  {platformStats.total_bookings > 0 
                    ? `${platformStats.currency} ${(platformStats.total_revenue / platformStats.total_bookings).toFixed(2)}`
                    : 'N/A'
                  }
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <div>
            <div className="bg-white p-6 rounded-lg shadow-md mb-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold">User Management</h2>
                <div className="flex gap-3">
                  <button
                    onClick={() => setShowAddUserModal(true)}
                    className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-4 py-2 rounded-lg hover:shadow-lg transition flex items-center gap-2"
                    data-testid="add-user-btn"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                    Add User
                  </button>
                  <select
                    value={userFilter}
                    onChange={(e) => setUserFilter(e.target.value)}
                    className="px-4 py-2 border rounded-lg"
                    data-testid="user-filter"
                  >
                    <option value="all">All Users</option>
                    <option value="provider">Providers</option>
                    <option value="client">Clients</option>
                    <option value="admin">Admins</option>
                  </select>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full" data-testid="users-table">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Phone</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Joined</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredUsers.map((u) => (
                      <tr key={u.id} data-testid={`user-row-${u.id}`}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 bg-gradient-to-r from-orange-500 to-orange-600 rounded-full flex items-center justify-center text-white text-sm font-semibold">
                              {u.name?.charAt(0)?.toUpperCase() || '?'}
                            </div>
                            <div>
                              <div className="text-sm font-medium text-gray-900">{u.name}</div>
                              {u.is_owner && <span className="text-xs text-yellow-600">👑 Owner</span>}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{u.email}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{u.phone || '-'}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            u.role === 'admin' ? 'bg-purple-100 text-purple-800' :
                            u.role === 'provider' ? 'bg-blue-100 text-blue-800' :
                            'bg-green-100 text-green-800'
                          }`}>
                            {u.role}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            u.status === 'active' ? 'bg-green-100 text-green-800' :
                            u.status === 'suspended' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-red-100 text-red-800'
                          }`}>
                            {u.status || 'active'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {new Date(u.created_at).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => openEditModal(u)}
                              className="text-blue-600 hover:text-blue-800 p-1"
                              title="Edit User"
                              data-testid={`edit-user-${u.id}`}
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                            </button>
                            {!u.is_owner && u.email !== 'chitopargas@gmail.com' && (
                              <button
                                onClick={() => handleDeleteUser(u.id)}
                                className="text-red-600 hover:text-red-800 p-1"
                                title="Delete User"
                                data-testid={`delete-user-${u.id}`}
                              >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              </button>
                            )}
                            <select
                              onChange={(e) => {
                                if (e.target.value) {
                                  handleUserStatusUpdate(u.id, e.target.value);
                                }
                                e.target.value = '';
                              }}
                              className="border rounded px-2 py-1 text-xs"
                              data-testid={`status-select-${u.id}`}
                            >
                              <option value="">Status</option>
                              <option value="active">Active</option>
                              <option value="suspended">Suspend</option>
                              <option value="banned">Ban</option>
                            </select>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              {filteredUsers.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  No users found with the selected filter.
                </div>
              )}
            </div>
          </div>
        )}
        {/* Withdrawals Tab */}
        {activeTab === 'withdrawals' && (
          <div>
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h2 className="text-xl font-bold mb-6">Withdrawal Requests</h2>
              {withdrawals.filter(w => w.status === 'pending').length === 0 ? (
                <p className="text-gray-500 text-center py-8">No pending requests</p>
              ) : (
                <div className="space-y-4 mb-8">
                  {withdrawals.filter(w => w.status === 'pending').map((withdrawal) => (
                    <div key={withdrawal.id} className="border rounded-lg p-4">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-bold text-lg">{withdrawal.currency} {withdrawal.amount.toFixed(2)}</p>
                          <p className="text-sm text-gray-700"><strong>Provider:</strong> {withdrawal.provider_name || withdrawal.user_name}</p>
                          <p className="text-sm text-gray-600"><strong>Method:</strong> {withdrawal.payment_method}</p>
                          <p className="text-sm text-gray-600"><strong>Account:</strong> {withdrawal.account_name || ''} - {withdrawal.account_number || ''}{withdrawal.bank_name ? ` (${withdrawal.bank_name})` : ''}</p>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleWithdrawalAction(withdrawal.id, 'approved')}
                            className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600"
                          >
                            Approve
                          </button>
                          <button
                            onClick={() => handleWithdrawalAction(withdrawal.id, 'rejected')}
                            className="bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600"
                          >
                            Reject
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <h3 className="text-lg font-bold mb-4 mt-8">All Withdrawal History</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Provider</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {withdrawals.map((w) => (
                      <tr key={w.id}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">{new Date(w.created_at).toLocaleDateString()}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">{w.provider_name}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold">{w.currency} {w.amount.toFixed(2)}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            w.status === 'completed' || w.status === 'approved' ? 'bg-green-100 text-green-800' :
                            w.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-red-100 text-red-800'
                          }`}>
                            {w.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Reports Tab */}
        {activeTab === 'reports' && transactionReport && revenueReport && (
          <div>
            {/* Date Filter for Reports */}
            <div className="bg-white p-4 rounded-lg shadow-md mb-6" data-testid="reports-date-filter">
              <div className="flex flex-wrap items-center gap-3">
                <span className="text-sm font-semibold text-gray-700">Filter by:</span>
                <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
                  {[
                    { key: 'all', label: 'All Time' },
                    { key: 'today', label: 'Today' },
                    { key: 'week', label: 'This Week' },
                    { key: 'month', label: 'This Month' },
                    { key: 'quarter', label: 'Quarter' },
                    { key: 'year', label: 'This Year' },
                  ].map(p => (
                    <button
                      key={p.key}
                      onClick={() => setDateRange(getDatePreset(p.key))}
                      className={`px-3 py-1.5 rounded-md text-xs font-medium transition ${
                        dateRange.preset === p.key
                          ? 'bg-blue-600 text-white shadow'
                          : 'text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
                <div className="flex items-center gap-2 ml-auto">
                  <input
                    type="date"
                    value={dateRange.start}
                    onChange={(e) => setDateRange({ start: e.target.value, end: dateRange.end || e.target.value, preset: 'custom' })}
                    className="border rounded-md px-2 py-1.5 text-xs"
                  />
                  <span className="text-gray-400 text-xs">to</span>
                  <input
                    type="date"
                    value={dateRange.end}
                    onChange={(e) => setDateRange({ start: dateRange.start || e.target.value, end: e.target.value, preset: 'custom' })}
                    className="border rounded-md px-2 py-1.5 text-xs"
                  />
                </div>
                {dateRange.preset !== 'all' && (
                  <span className="text-xs text-blue-600 font-medium bg-blue-50 px-2 py-1 rounded">
                    {dateRange.start} - {dateRange.end}
                  </span>
                )}
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-md mb-6">
              <h2 className="text-xl font-bold mb-4">Transaction Report</h2>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-blue-50 p-4 rounded">
                  <p className="text-sm text-gray-600">Total Transactions</p>
                  <p className="text-2xl font-bold text-blue-600">{transactionReport.summary.total_transactions}</p>
                </div>
                <div className="bg-green-50 p-4 rounded">
                  <p className="text-sm text-gray-600">Total Amount</p>
                  <p className="text-2xl font-bold text-green-600">${transactionReport.summary.total_amount.toFixed(2)}</p>
                </div>
                <div className="bg-purple-50 p-4 rounded">
                  <p className="text-sm text-gray-600">Platform Commission</p>
                  <p className="text-2xl font-bold text-purple-600">${transactionReport.summary.total_platform_commission.toFixed(2)}</p>
                </div>
                <div className="bg-orange-50 p-4 rounded">
                  <p className="text-sm text-gray-600">Provider Earnings</p>
                  <p className="text-2xl font-bold text-orange-600">${transactionReport.summary.total_provider_amount.toFixed(2)}</p>
                </div>
              </div>

              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="amount" fill="#2563eb" name="Total Amount" />
                  <Bar dataKey="commission" fill="#dc2626" name={`Commission (${feeRates.total_platform_rate}%)`} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-md">
              <h2 className="text-xl font-bold mb-4">Revenue Report</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="font-bold mb-4">Revenue by Currency</h3>
                  {revenueReport.revenue_by_currency && Object.entries(revenueReport.revenue_by_currency).map(([currency, amount]) => (
                    <div key={currency} className="flex justify-between mb-2 p-3 bg-gray-50 rounded">
                      <span className="font-semibold">{currency}</span>
                      <span>{amount.toFixed(2)}</span>
                    </div>
                  ))}
                </div>
                <div>
                  <h3 className="font-bold mb-4">Revenue by Category</h3>
                  {revenueReport.revenue_by_category && Object.entries(revenueReport.revenue_by_category).map(([category, amount]) => (
                    <div key={category} className="flex justify-between mb-2 p-3 bg-gray-50 rounded">
                      <span>{category}</span>
                      <span className="font-semibold">${amount.toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Payments Tab - Bank Transfer Verification */}
        {activeTab === 'payments' && (
          <div className="bg-white rounded-lg shadow-md p-6" data-testid="payments-tab">
            <h2 className="text-xl font-bold mb-6">Bank Transfer Verifications</h2>
            
            {pendingBankTransfers.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <p className="text-lg">No pending bank transfers to verify</p>
                <p className="text-sm mt-2">All payments have been processed</p>
              </div>
            ) : (
              <div className="space-y-4">
                {pendingBankTransfers.map((transfer) => (
                  <div 
                    key={transfer.id} 
                    className="border border-gray-200 rounded-lg p-4"
                    data-testid={`transfer-${transfer.id}`}
                  >
                    <div className="flex flex-col md:flex-row justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="bg-yellow-100 text-yellow-800 text-xs px-2 py-1 rounded font-semibold">
                            PENDING VERIFICATION
                          </span>
                        </div>
                        <h3 className="font-bold text-lg">{transfer.service_title || 'Service Booking'}</h3>
                        <p className="text-gray-600 text-sm">Client: {transfer.client_name} ({transfer.client_email})</p>
                        <div className="grid grid-cols-2 gap-4 mt-3">
                          <div>
                            <span className="text-gray-500 text-sm">Amount</span>
                            <p className="font-bold text-green-600">PHP {transfer.amount?.toFixed(2)}</p>
                          </div>
                          <div>
                            <span className="text-gray-500 text-sm">Reference</span>
                            <p className="font-mono text-sm">{transfer.bank_transfer_reference}</p>
                          </div>
                        </div>
                      </div>
                      
                      {transfer.bank_transfer_proof && (
                        <div className="md:w-48">
                          <p className="text-sm text-gray-500 mb-2">Proof of Payment:</p>
                          <a 
                            href={`${BACKEND_URL}${transfer.bank_transfer_proof}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block"
                          >
                            <img 
                              src={`${BACKEND_URL}${transfer.bank_transfer_proof}`}
                              alt="Payment proof"
                              className="w-full h-32 object-cover rounded border hover:opacity-75"
                            />
                          </a>
                        </div>
                      )}
                    </div>
                    
                    <div className="flex gap-3 mt-4 pt-4 border-t">
                      <button
                        onClick={async () => {
                          try {
                            await axios.post(`${API}/admin/payments/${transfer.id}/verify?approved=true`);
                            fetchAllData();
                          } catch (err) {
                            alert(err.response?.data?.detail || 'Error approving payment');
                          }
                        }}
                        className="flex-1 bg-green-500 text-white py-2 rounded-lg hover:bg-green-600 font-semibold"
                        data-testid={`approve-transfer-${transfer.id}`}
                      >
                        ✓ Approve Payment
                      </button>
                      <button
                        onClick={async () => {
                          if (!window.confirm('Are you sure you want to reject this payment?')) return;
                          try {
                            await axios.post(`${API}/admin/payments/${transfer.id}/verify?approved=false`);
                            fetchAllData();
                          } catch (err) {
                            alert(err.response?.data?.detail || 'Error rejecting payment');
                          }
                        }}
                        className="flex-1 bg-red-500 text-white py-2 rounded-lg hover:bg-red-600 font-semibold"
                        data-testid={`reject-transfer-${transfer.id}`}
                      >
                        ✗ Reject
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Escrow Releases Section */}
            <div className="mt-8">
              <h2 className="text-xl font-bold mb-6">Escrow Releases</h2>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-blue-800">
                  <strong>ℹ️ Auto-Release:</strong> Escrow funds are automatically released to providers 1 day after job completion.
                </p>
              </div>
              
              {pendingEscrowReleases.length === 0 ? (
                <div className="text-center py-8 text-gray-500 bg-white rounded-lg border">
                  <p className="text-lg">No pending escrow releases</p>
                  <p className="text-sm mt-1">All completed jobs have had funds released</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {pendingEscrowReleases.map((escrow) => (
                    <div 
                      key={escrow.id} 
                      className="bg-white border border-gray-200 rounded-lg p-4"
                      data-testid={`escrow-${escrow.id}`}
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <h3 className="font-bold">{escrow.service_title || 'Service Booking'}</h3>
                          <p className="text-sm text-gray-600">Provider: {escrow.provider_name}</p>
                          <div className="grid grid-cols-2 gap-4 mt-2">
                            <div>
                              <span className="text-gray-500 text-xs">Payout Amount</span>
                              <p className="font-bold text-green-600">PHP {escrow.provider_payout_amount?.toFixed(2)}</p>
                            </div>
                            <div>
                              <span className="text-gray-500 text-xs">Booking Status</span>
                              <p className={`font-semibold ${
                                escrow.booking_status === 'completed' ? 'text-blue-600' : 'text-yellow-600'
                              }`}>
                                {escrow.booking_status?.replace('_', ' ')}
                              </p>
                            </div>
                          </div>
                          {escrow.hours_until_release !== undefined && escrow.booking_status === 'completed' && (
                            <p className="text-sm text-purple-600 mt-2">
                              ⏰ Auto-release in: {escrow.hours_until_release.toFixed(1)} hours
                            </p>
                          )}
                        </div>
                        <div className="text-right">
                          <span className="bg-yellow-100 text-yellow-800 text-xs px-2 py-1 rounded font-semibold">
                            PENDING RELEASE
                          </span>
                        </div>
                      </div>
                      
                      {escrow.booking_status === 'completed' && (
                        <div className="mt-4 pt-4 border-t">
                          <button
                            onClick={async () => {
                              if (!window.confirm('Release escrow funds to provider now?')) return;
                              try {
                                await axios.post(`${API}/payments/${escrow.id}/release`);
                                fetchAllData();
                                alert('Escrow released successfully!');
                              } catch (err) {
                                alert(err.response?.data?.detail || 'Error releasing escrow');
                              }
                            }}
                            className="w-full bg-green-500 text-white py-2 rounded-lg hover:bg-green-600 font-semibold"
                            data-testid={`release-escrow-${escrow.id}`}
                          >
                            💰 Release Escrow Now
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Add Admin Modal */}
      {showAddAdminModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h2 className="text-2xl font-bold mb-4">Add Administrator</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold mb-2">Name</label>
                <input
                  type="text"
                  value={newAdmin.name}
                  onChange={(e) => setNewAdmin({...newAdmin, name: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold mb-2">Email</label>
                <input
                  type="email"
                  value={newAdmin.email}
                  onChange={(e) => setNewAdmin({...newAdmin, email: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold mb-2">Password</label>
                <input
                  type="password"
                  value={newAdmin.password}
                  onChange={(e) => setNewAdmin({...newAdmin, password: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setShowAddAdminModal(false)}
                  className="flex-1 bg-gray-200 py-2 rounded-lg hover:bg-gray-300"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddAdmin}
                  className="flex-1 bg-gradient-to-r from-orange-500 to-orange-600 text-white py-2 rounded-lg hover:shadow-lg"
                >
                  Create Admin
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add User Modal */}
      {showAddUserModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-testid="add-user-modal">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-bold mb-4">Add New User</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold mb-2">Name *</label>
                <input
                  type="text"
                  value={newUser.name}
                  onChange={(e) => setNewUser({...newUser, name: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Full name"
                  data-testid="new-user-name"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold mb-2">Email *</label>
                <input
                  type="email"
                  value={newUser.email}
                  onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="email@example.com"
                  data-testid="new-user-email"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold mb-2">Password *</label>
                <input
                  type="password"
                  value={newUser.password}
                  onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Minimum 6 characters"
                  data-testid="new-user-password"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold mb-2">Phone</label>
                <input
                  type="tel"
                  value={newUser.phone}
                  onChange={(e) => setNewUser({...newUser, phone: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="09123456789"
                  data-testid="new-user-phone"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold mb-2">Role *</label>
                <select
                  value={newUser.role}
                  onChange={(e) => setNewUser({...newUser, role: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  data-testid="new-user-role"
                >
                  <option value="client">Client</option>
                  <option value="provider">Service Provider</option>
                  <option value="admin">Administrator</option>
                </select>
              </div>
              <div className="flex gap-2 pt-4">
                <button
                  onClick={() => {
                    setShowAddUserModal(false);
                    setNewUser({ email: '', password: '', name: '', role: 'client', phone: '' });
                  }}
                  className="flex-1 bg-gray-200 py-2 rounded-lg hover:bg-gray-300 font-semibold"
                  data-testid="cancel-add-user"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddUser}
                  className="flex-1 bg-gradient-to-r from-blue-600 to-blue-700 text-white py-2 rounded-lg hover:shadow-lg font-semibold"
                  data-testid="submit-add-user"
                >
                  Create User
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {showEditUserModal && editingUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-testid="edit-user-modal">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-bold mb-4">Edit User</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold mb-2">Name</label>
                <input
                  type="text"
                  value={editingUser.name || ''}
                  onChange={(e) => setEditingUser({...editingUser, name: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  data-testid="edit-user-name"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold mb-2">Email</label>
                <input
                  type="email"
                  value={editingUser.email || ''}
                  onChange={(e) => setEditingUser({...editingUser, email: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  data-testid="edit-user-email"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold mb-2">Phone</label>
                <input
                  type="tel"
                  value={editingUser.phone || ''}
                  onChange={(e) => setEditingUser({...editingUser, phone: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="09123456789"
                  data-testid="edit-user-phone"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold mb-2">Bio</label>
                <textarea
                  value={editingUser.bio || ''}
                  onChange={(e) => setEditingUser({...editingUser, bio: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  rows={3}
                  placeholder="Short bio or description"
                  data-testid="edit-user-bio"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold mb-2">Role</label>
                <select
                  value={editingUser.role || 'client'}
                  onChange={(e) => setEditingUser({...editingUser, role: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  disabled={editingUser.is_owner || editingUser.email === 'chitopargas@gmail.com'}
                  data-testid="edit-user-role"
                >
                  <option value="client">Client</option>
                  <option value="provider">Service Provider</option>
                  <option value="admin">Administrator</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-semibold mb-2">Status</label>
                <select
                  value={editingUser.status || 'active'}
                  onChange={(e) => setEditingUser({...editingUser, status: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  disabled={editingUser.is_owner || editingUser.email === 'chitopargas@gmail.com'}
                  data-testid="edit-user-status"
                >
                  <option value="active">Active</option>
                  <option value="suspended">Suspended</option>
                  <option value="banned">Banned</option>
                </select>
              </div>
              
              {/* User Info Display */}
              <div className="bg-gray-50 p-3 rounded-lg text-sm">
                <p><strong>User ID:</strong> {editingUser.id}</p>
                <p><strong>Joined:</strong> {new Date(editingUser.created_at).toLocaleDateString()}</p>
                {editingUser.available_roles && (
                  <p><strong>Available Roles:</strong> {editingUser.available_roles.join(', ')}</p>
                )}
              </div>
              
              <div className="flex gap-2 pt-4">
                <button
                  onClick={() => {
                    setShowEditUserModal(false);
                    setEditingUser(null);
                  }}
                  className="flex-1 bg-gray-200 py-2 rounded-lg hover:bg-gray-300 font-semibold"
                  data-testid="cancel-edit-user"
                >
                  Cancel
                </button>
                <button
                  onClick={handleEditUser}
                  className="flex-1 bg-gradient-to-r from-blue-600 to-blue-700 text-white py-2 rounded-lg hover:shadow-lg font-semibold"
                  data-testid="submit-edit-user"
                >
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default AdminDashboardFull;
